import React, { ReactElement } from 'react';
import { View, StyleSheet, Text, ViewStyle, TouchableWithoutFeedback, StyleProp, TextStyle } from 'react-native';
import Animated, { useSharedValue, useAnimatedStyle, withSpring, runOnJS } from 'react-native-reanimated';
import { useThemeContext } from '_utils/themeContext';
import Icon from './Icon/Icon';
import _fonts from '../_fonts';
import { Theme, useThemeStyle, WW } from '../_utils';

interface CheckBoxProps {
    style?: ViewStyle;
    titleStyle?: StyleProp<TextStyle>;
    onPress(): void;
    checked: boolean;
    title?: string | ReactElement;
    disabled?: boolean;
    endContent?: ReactElement | null;
}

const CheckBox = ({
    style,
    onPress,
    checked,
    title,
    titleStyle,
    disabled,
    endContent,
}: CheckBoxProps): ReactElement => {
    const themedStyle = useThemeStyle(styles);
    const { theme } = useThemeContext();
    const scale = useSharedValue(1);

    const handlePress = () => {
        scale.value = withSpring(0.9, { damping: 5, stiffness: 200 }, () => {
            runOnJS(onPress)();
            scale.value = withSpring(1);
        });
    };

    const animatedIconStyle = useAnimatedStyle(() => ({
        transform: [{ scale: scale.value }],
    }));

    return (
        <TouchableWithoutFeedback
            role="checkbox"
            accessibilityState={{ checked }}
            hitSlop={{ top: 10, bottom: 20 }}
            onPress={handlePress}
            disabled={disabled}
        >
            <View style={[themedStyle.container, style]}>
                <Animated.View style={animatedIconStyle}>
                    <Icon
                        name={checked ? 'checked' : 'unchecked'}
                        color={checked ? 'main' : disabled ? 'mediumGrey' : 'black'}
                    />
                </Animated.View>
                <Text
                    maxFontSizeMultiplier={2}
                    style={[themedStyle.title, disabled && { color: theme.mediumGrey }, titleStyle]}
                >
                    {title}
                </Text>
                {endContent}
            </View>
        </TouchableWithoutFeedback>
    );
};

const styles = (theme: Theme) =>
    StyleSheet.create({
        container: {
            flexDirection: 'row',
            alignItems: 'center',
            flexShrink: 0,
            flexWrap: 'nowrap',
            minHeight: 40,
        },
        title: {
            marginLeft: WW * 0.02,
            fontFamily: _fonts.primaryFont,
            fontSize: WW * 0.03,
            color: theme.darkGrey,
        },
    });

export default CheckBox;
